/**
 * Cache invalidation utilities
 * Provides helper functions for invalidating entity caches and related entity caches
 */

import { cacheManager } from './cacheManager';
import { 
  CacheEntityType, 
  CACHE_KEY_PATTERNS, 
  BASIC_ENTITY_RELATIONSHIPS 
} from './cacheConfig';

/**
 * Invalidate all caches for a specific entity type
 * @param entityType - The type of entity to invalidate
 * @returns The number of cache entries invalidated
 */
export function invalidateEntityCache(entityType: CacheEntityType): number {
  // Invalidate the "all" collection
  const prefix = `${entityType}s:`;
  return cacheManager.deleteByPrefix(prefix);
}

/**
 * Invalidate cache for a specific entity by ID
 * @param entityType - The type of entity to invalidate
 * @param entityId - The ID of the entity to invalidate
 */
export function invalidateEntityById(
  entityType: CacheEntityType, 
  entityId: string
): void {
  // Invalidate the specific entity
  const key = CACHE_KEY_PATTERNS.BY_ID(entityType, entityId);
  cacheManager.delete(key);
  
  // Also invalidate collections that might contain this entity
  invalidateEntityCache(entityType);
}

/**
 * Invalidate related entity caches based on relationships
 * @param entityType - The type of entity that was modified
 * @param entityId - The ID of the entity that was modified
 * @param entityData - The entity data (for relationship fields)
 */
export function invalidateRelatedCaches(
  entityType: CacheEntityType,
  entityId: string,
  entityData?: Record<string, any>
): void {
  // First invalidate the entity itself
  invalidateEntityById(entityType, entityId);
  
  // Get relationships for this entity type
  const relationships = BASIC_ENTITY_RELATIONSHIPS[entityType] || [];
  
  // Invalidate related entities
  for (const relation of relationships) {
    if (relation.isParent) {
      // This entity is a parent of the related entity
      // Invalidate the related entity's "by relation" cache
      const prefix = CACHE_KEY_PATTERNS.BY_RELATION(relation.entity, entityType, entityId);
      cacheManager.deleteByPrefix(prefix);
    } else if (entityData && entityData[relation.field]) {
      // This entity has a reference to the related entity
      // Invalidate the related entity by ID
      invalidateEntityById(relation.entity, entityData[relation.field]);
      
      // Also invalidate any "by relation" caches
      const prefix = CACHE_KEY_PATTERNS.BY_RELATION(entityType, relation.entity, entityData[relation.field]);
      cacheManager.deleteByPrefix(prefix);
    }
  }
} 